import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Stack,
  Box,
  Radio,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Modal,
  Banner,
  Grid,
  BadgeStatus,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  PeopleIcon,
  AlertCriticalIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { phoneFormatter } from '../../../../utils/redux-form-helper';
import messages from './messages';
import PortalBadge from './PortalBadge';

type Props = {
  open: boolean,
  onClose: () => void,
  onConfirm: (selectedPortalUserId: ?string) => void,
  intl: Object,
  selectedValues: Object,
  contactName: string,
  selectedProfileId: ?string,
  primaryProfile: ?Object,
  duplicateProfile: ?Object,
  currentDuplicateData: ?Object,
};

const ConfirmMergeModal = ({
  open,
  onClose,
  onConfirm,
  intl,
  selectedValues,
  contactName,
  selectedProfileId,
  primaryProfile,
  duplicateProfile,
  currentDuplicateData,
}: Props) => {
  const [selectedPortalUserId, setSelectedPortalUserId] = useState(null);

  // Get the selected profile's household members
  const selectedProfileHousehold = useMemo(() => {
    if (!selectedProfileId) return [];

    const selectedProfile =
      selectedProfileId === currentDuplicateData?.mainProspectId
        ? primaryProfile
        : duplicateProfile;

    return selectedProfile?.additionalOccupants || [];
  }, [
    selectedProfileId,
    primaryProfile,
    duplicateProfile,
    currentDuplicateData?.mainProspectId,
  ]);

  const activePortalUsers = useMemo(() => {
    const portalUsers = currentDuplicateData?.portalUsers || [];

    // Filter portal users by selected emails only (disregard prospect ID)
    const filteredPortalUsers = portalUsers.filter((user) => {
      const matchesSelectedEmail = selectedValues?.emails?.some(
        (email) => email.emailAddress === user.email,
      );
      return matchesSelectedEmail;
    });

    // Return full portal user objects with id, email, and prospectId
    return filteredPortalUsers.map((user) => ({
      id: user.id,
      emailAddress: user.email,
      prospectId: user.prospectId,
    }));
  }, [selectedValues?.emails, currentDuplicateData?.portalUsers]);

  // Automatically select portal user if there's only one
  useEffect(() => {
    if (activePortalUsers.length === 1 && !selectedPortalUserId) {
      setSelectedPortalUserId(activePortalUsers[0].id);
    }
  }, [activePortalUsers, selectedPortalUserId]);

  const renderField = (
    label: string,
    values: Array<string> | string | null,
  ) => {
    if (!values || (Array.isArray(values) && values.length === 0)) {
      return null;
    }

    const displayValues = Array.isArray(values) ? values : [values];

    return (
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="labelMedium" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {displayValues.map((value, index) => (
          <Typography
            key={index}
            variant="bodyMedium"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {value}
          </Typography>
        ))}
      </Stack>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(messages.mergePotentialDuplicatesTitle)}
      icon={<PeopleIcon />}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancelButton),
          variant: 'text',
          onClick: onClose,
        },
        {
          children: intl.formatMessage(messages.confirmButton),
          onClick: () => onConfirm(selectedPortalUserId),
          color: 'primary',
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            maxWidth: 700,
          },
        },
      }}
    >
      <Stack spacing={3}>
        <Typography
          variant="bodyMedium"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(messages.confirmMergeMessage, {
              contactName,
            }),
          }}
        />

        <Banner
          text={intl.formatMessage(messages.mergeWarningBanner)}
          icon={<AlertCriticalIcon color="inherit" />}
          hasBorder={false}
          color="warning"
          TypographyProps={{
            variant: 'bodySmall',
            sx: {
              textWrap: 'wrap',
            },
          }}
        />

        <Box
          sx={{
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Grid container spacing={3}>
            {/* Column 1: Name */}
            <Grid item xs={12} sm={4}>
              {(selectedValues?.firstName || selectedValues?.lastName) &&
                renderField(
                  'Name',
                  `${selectedValues.firstName || ''} ${
                    selectedValues.lastName || ''
                  }`.trim(),
                )}
            </Grid>

            {/* Column 2: Preferred Name and Email */}
            <Grid item xs={12} sm={4}>
              {selectedValues?.preferredName &&
                renderField('Preferred Name', selectedValues.preferredName)}
              {selectedValues?.emails?.length > 0 &&
                renderField(
                  'Email',
                  selectedValues.emails.map((email) => email.emailAddress),
                )}
            </Grid>

            {/* Column 3: Phone */}
            <Grid item xs={12} sm={4}>
              {selectedValues?.phones?.length > 0 &&
                renderField(
                  'Phone',
                  selectedValues.phones.map((phone) =>
                    phoneFormatter(phone.phoneNumber),
                  ),
                )}
            </Grid>

            {/* Household Members - Full width */}
            {selectedProfileHousehold.length > 0 && (
              <Grid item xs={12}>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography variant="labelMedium" sx={{ fontWeight: 600 }}>
                    Household
                  </Typography>
                  {selectedProfileHousehold.map((occupant, index) => {
                    const name =
                      `${occupant.firstName} ${occupant.lastName}`.trim();
                    const relationship = occupant.relationship?.name;
                    const displayName = relationship
                      ? `${name} (${relationship})`
                      : name;

                    return (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography
                          variant="bodyMedium"
                          sx={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {displayName}
                        </Typography>
                        {occupant.hasPortalAccount && (
                          <PortalBadge intl={intl} />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Portal Account Selection Section - show if there are any portal users */}
        {activePortalUsers.length > 0 && (
          <>
            <Typography variant="titleMedium">
              {intl.formatMessage(messages.portalEmailSelectionTitle)}
            </Typography>
            <Typography
              variant="bodyMedium"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {intl.formatMessage(messages.portalEmailSelectionDescription)}
            </Typography>
            <Stack spacing={1.5}>
              {activePortalUsers.map((portalUser) => (
                <Stack
                  key={portalUser.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPortalUserId(portalUser.id)}
                >
                  <Radio
                    checked={selectedPortalUserId === portalUser.id}
                    onChange={() => setSelectedPortalUserId(portalUser.id)}
                    size="small"
                  />
                  <Typography variant="bodySmall">
                    {portalUser.emailAddress}
                  </Typography>
                  <BadgeStatus size="small" status="inProgress">
                    {intl.formatMessage(messages.portalBadge)}
                  </BadgeStatus>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default ConfirmMergeModal;
